import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useAuth } from '../../context/AuthContext';
import { useBrand } from '../../context/BrandContext';
import OrganizationSwitcher from '../OrganizationSwitcher';
import UserMenu from '../UserMenu';
import IntercomChat from '../IntercomChat';
import { Building2, Bot, MessageSquare, Shield, Workflow } from 'lucide-react';

const POSTHOG_WORKFLOW_FLAG = 'experiment_new-module_workflow-builder_temp';
const POSTHOG_CHATBOT_FLAG = 'experiment_new-module_chatbot-builder_temp';

function Navigation() {
  const { isAuthenticated, user } = useAuth();
  const { brandConfig } = useBrand();
  const location = useLocation();
  const workflowFlagEnabled = useFeatureFlagEnabled(POSTHOG_WORKFLOW_FLAG);
  const chatbotFlagEnabled = useFeatureFlagEnabled(POSTHOG_CHATBOT_FLAG);

  if (!isAuthenticated) return null;

  const isActivePage = (path) => {
    if (path === '/chat') {
      return location.pathname === '/' || location.pathname === '/chat' || location.pathname.startsWith('/chat/');
    }
    if (path === '/forms') {
      return location.pathname === '/forms' || location.pathname.startsWith('/forms/');
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const activeClass = 'text-brand-600 bg-brand-50 dark:text-brand-400 dark:bg-brand-900/20';
  const inactiveClass = 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200';

  return (
    <nav className="bg-white dark:bg-gray-900 shadow-sm border-b border-gray-200 dark:border-gray-700">
      <div className="w-full mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16" style={{ margin: '0 23px' }}>
          <div className="flex items-center space-x-8">
            <Link to="/" className="flex items-center">
              <span className="text-xl font-bold text-gray-900 dark:text-white dark:ml-2">
                {brandConfig.shortName}
              </span>
            </Link>
            {brandConfig.features.forms && (
              <Link
                to="/forms"
                className={`px-3 py-2 rounded-md text-sm font-medium ${isActivePage('/forms') ? activeClass : inactiveClass}`}
              >
                Forms
              </Link>
            )}
            {brandConfig.features.pages && (
              <Link
                to="/sites"
                className={`px-3 py-2 rounded-md text-sm font-medium ${isActivePage('/sites') ? activeClass : inactiveClass}`}
              >
                Sites
              </Link>
            )}
            {brandConfig.features.crm && (
              <Link
                to="/crm"
                className={`px-3 py-2 rounded-md text-sm font-medium flex items-center ${isActivePage('/crm') ? activeClass : inactiveClass}`}
              >
                <Building2 className="w-4 h-4 mr-1" />
                CRM
              </Link>
            )}
            {brandConfig.features.chat && (
              <Link
                to="/chat"
                className={`px-3 py-2 rounded-md text-sm font-medium flex items-center ${isActivePage('/chat') ? activeClass : inactiveClass}`}
              >
                <MessageSquare className="w-4 h-4 mr-1" />
                Chat
              </Link>
            )}
            {brandConfig.features.workflows && workflowFlagEnabled && (
              <Link
                to="/workflows"
                className={`px-3 py-2 rounded-md text-sm font-medium flex items-center ${isActivePage('/workflows') ? activeClass : inactiveClass}`}
              >
                <Workflow className="w-4 h-4 mr-1" />
                Workflows
              </Link>
            )}
            {brandConfig.features.chatbots && chatbotFlagEnabled && (
              <Link
                to="/chatbots"
                className={`px-3 py-2 rounded-md text-sm font-medium flex items-center ${isActivePage('/chatbots') ? activeClass : inactiveClass}`}
              >
                <Bot className="w-4 h-4 mr-1" />
                Chatbots
              </Link>
            )}
          </div>
          <div className="flex items-center space-x-6">
            {user?.is_superadmin && (
              <Link
                to="/admin"
                className={`px-3 py-2 rounded-md text-sm font-medium flex items-center ${isActivePage('/admin') ? activeClass : inactiveClass}`}
              >
                <Shield className="w-4 h-4 mr-1" />
                Admin
              </Link>
            )}
            <OrganizationSwitcher />
            <UserMenu />
          </div>
        </div>
      </div>
    </nav>
  );
}

export default function NavbarLayout({ children }) {
  const location = useLocation();
  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-gray-900 text-gray-900">
      <Navigation />
      <main className="flex-1 w-full mx-auto flex flex-col">
        <div key={location.pathname} className="animate-fadeIn flex-1 flex flex-col">
          {children}
        </div>
      </main>
      <IntercomChat />
    </div>
  );
}
